import React, { useState, useEffect, useCallback } from 'react';
import { Consignee } from '../../types';
import { consigneeService } from '../../services';
import { RequiredIndicator } from '../ui';
import { AsyncSearchSelect } from '../forms';

interface ConsigneeSectionProps {
  consigneeId: string;
  onConsigneeSelect: (consignee: Consignee) => void;
  disabled?: boolean;
  name?: string;
  address?: string;
  street?: string;
  city?: string;
  transit?: string;
  onNameChange?: (name: string) => void;
  onAddressChange?: (address: string) => void;
  onStreetChange?: (street: string) => void;
  onCityChange?: (city: string) => void;
  onTransitChange?: (transit: string) => void;
  onPodCodeChange?: (podCode: string) => void;
  onShipperIdChange?: (
    shipperId: string,
    shipperCode?: string,
    shipperName?: string
  ) => void;
  initialConsigneeDisplay?: string;
}

export const ConsigneeSection: React.FC<ConsigneeSectionProps> = ({
  consigneeId,
  onConsigneeSelect,
  disabled = false,
  name: externalName = '',
  address: externalAddress = '',
  onNameChange,
  onAddressChange,
  onStreetChange,
  onCityChange,
  onTransitChange,
  onPodCodeChange,
  onShipperIdChange,
  initialConsigneeDisplay = '',
}) => {
  const [name, setName] = useState(externalName);
  const [address, setAddress] = useState(externalAddress);

  useEffect(() => {
    setName(externalName);
  }, [externalName]);

  useEffect(() => {
    setAddress(externalAddress);
  }, [externalAddress]);

  // Load consignees from backend
  const loadConsignees = useCallback(async (query: string) => {
    const results = await consigneeService.getAll({ query, limit: 20 });
    return results;
  }, []);

  // Build secondary label string from consignee fields
  const getConsigneeSecondaryLabel = (consignee: Consignee): string => {
    const parts = [
      consignee.name,
      consignee.address && `Addr: ${consignee.address}`,
      consignee.city && `City: ${consignee.city}`,
    ].filter(Boolean);
    return parts.join(' | ');
  };

  const handleSelectConsignee = (consignee: Consignee) => {
    const newName = consignee.name;
    // Combine address fields into single address field
    const addressParts = [
      consignee.address,
      consignee.street,
      consignee.city,
      consignee.transit,
    ].filter(Boolean);
    const newAddress = addressParts.join('\n');
    setName(newName);
    setAddress(newAddress);
    // Call onConsigneeSelect which handles setting consigneeId, name, and address in parent
    onConsigneeSelect(consignee);
    // Keep backward compatibility for other fields
    if (onStreetChange) onStreetChange(consignee.street || '');
    if (onCityChange) onCityChange(consignee.city || '');
    if (onTransitChange) onTransitChange(consignee.transit || '');
    if (onPodCodeChange && consignee.port_id) onPodCodeChange(consignee.port_id);
    if (onShipperIdChange && consignee.shipper_id)
      onShipperIdChange(
        consignee.shipper_id,
        consignee.shipper_code,
        consignee.shipper_name
      );
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (onNameChange) onNameChange(value);
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    if (onAddressChange) onAddressChange(value);
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1.5 pb-1 border-b border-gray-200 dark:border-gray-700 flex items-center">
        <svg
          className="w-3.5 h-3.5 mr-1.5 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        Consignee
      </h3>

      <div className="space-y-1.5">
        {/* Consignee Code */}
        <AsyncSearchSelect<Consignee>
          loadOptions={loadConsignees}
          getValue={(consignee) => consignee.id}
          getLabel={(consignee) => consignee.code}
          getSecondaryLabel={getConsigneeSecondaryLabel}
          onSelect={handleSelectConsignee}
          value={consigneeId}
          initialDisplayValue={initialConsigneeDisplay}
          label="Consignee Code"
          placeholder="Enter consignee code..."
          disabled={disabled}
          noOptionsMessage="No consignees found"
        />

        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
            Name <RequiredIndicator />
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            disabled={disabled}
            placeholder="Enter name..."
            required
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
            Address
          </label>
          <textarea
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            disabled={disabled}
            placeholder="Enter address..."
            rows={3}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 resize-none"
          />
        </div>
      </div>
    </div>
  );
};
